import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../ui/Toast';
import { updatePresence } from '../../services/presenceService';

interface ChatMessage {
  id: string;
  senderId: string;
  message: string;
  messageType?: string;
  videoCallUrl?: string;
  timestamp: any;
}

interface ChatPhaseProps {
  selectedConnectionData: {
    avatar?: string | null;
    name?: string;
    online?: boolean;
    photoURL?: string | null;
    photoPosition?: { x: number; y: number};
    id?: string;
  } | null;
  currentPhase: string;
  hasChatMessages: boolean;
  connectionFlow: any;
  user: any;
  onAcceptChatStep: () => Promise<void>;
  chatMessages: ChatMessage[];
  formatChatTime: (timestamp: any) => string;
  chatEndRef: React.RefObject<HTMLDivElement>;
  newMessage: string;
  onNewMessageChange: (value: string) => void;
  onSendMessage: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  className?: string;
}

const ChatPhase: React.FC<ChatPhaseProps> = ({
  selectedConnectionData,
  currentPhase,
  hasChatMessages,
  connectionFlow,
  user,
  onAcceptChatStep,
  chatMessages,
  formatChatTime,
  chatEndRef,
  newMessage,
  onNewMessageChange,
  onSendMessage,
  onKeyPress,
  className = '',
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(0);
  const [isAccepting, setIsAccepting] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // ✅ UX: Toast notifications
  const { success, error: showError } = useToast();
  
  // ✅ UX: Typing indicator (simple presence update)
  const handleTyping = () => {
    if (!connectionFlow?.id || !user?.uid) return;
    
    // Update presence to 'typing'
    updatePresence(
      connectionFlow.id,
      user.uid,
      user.displayName || user.email || 'User',
      'typing'
    );
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Reset to 'idle' after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      updatePresence(
        connectionFlow.id,
        user.uid,
        user.displayName || user.email || 'User',
        'idle'
      );
    }, 3000);
  };

  // Auto-scroll naar beneden wanneer er nieuwe berichten zijn
  useEffect(() => {
    // Alleen scrollen als er nieuwe berichten zijn (niet bij initial load)
    if (chatMessages.length > prevMessagesLengthRef.current && chatEndRef.current) {
      // Gebruik setTimeout om te wachten tot de DOM is geupdate
      setTimeout(() => {
        if (chatEndRef.current) {
          chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100);
    }
    prevMessagesLengthRef.current = chatMessages.length;
  }, [chatMessages, chatEndRef]);

  // Check of huidige gebruiker al geaccepteerd heeft
  const userAlreadyAccepted = !!(
    user?.uid &&
    connectionFlow?.chatStep?.acceptances?.[user.uid]
  );

  return (
    <div className={`flex flex-col ${className}`} style={{ overflow: 'hidden' }}>
      {/* Accept Button / Wacht-feedback - alleen in chat-fase, zolang er nog geen berichten zijn en de stap echt pending is */}
      {currentPhase === 'chat' &&
        !hasChatMessages &&
        connectionFlow?.chatStep?.status === 'pending' && (
        <div className="flex-shrink-0 p-2 border-b border-gray-200 bg-white">
          {userAlreadyAccepted ? (
            /* Al geaccepteerd - toon wachtbericht */
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-xs font-medium flex items-center space-x-2">
              <span className="text-sm">✅</span>
              <div>
                <p className="font-semibold">Geaccepteerd!</p>
                <p className="text-green-600 font-normal mt-0.5">
                  Wacht tot {selectedConnectionData?.name || 'de andere persoon'} online is en ook accepteert. 
                  Je kunt alvast een bericht typen.
                </p>
              </div>
            </div>
          ) : (
            /* Nog niet geaccepteerd - toon knop */
            <>
              <button
                type="button"
                data-testid="accept-connection"
                disabled={isAccepting}
                aria-label="Accepteer chat stap"
                aria-disabled={isAccepting}
                aria-describedby="accept-chat-help"
                onClick={async (e) => {
                  console.log('🔘 Accept button clicked in chat box');
                  e.preventDefault();
                  e.stopPropagation();
                  
                  if (!user) {
                    showError('Je bent niet ingelogd.');
                    return;
                  }
                  
                  setIsAccepting(true);
                  try {
                    await onAcceptChatStep();
                    console.log('✅ Chat step accepted successfully');
                    success('Chat geaccepteerd!');
                  } catch (error: any) {
                    console.error('❌ Error accepting chat step:', error);
                    showError(`Fout bij accepteren: ${error.message || 'Onbekende fout'}`);
                  } finally {
                    setIsAccepting(false);
                  }
                }}
                className={`bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium flex items-center space-x-2 ${
                  isAccepting ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <span>{isAccepting ? '⏳' : '✅'}</span>
                <span>{isAccepting ? 'Bezig met accepteren...' : 'Accepteren'}</span>
              </button>
              <span id="accept-chat-help" className="sr-only">
                Accepteer de chat stap om te beginnen met chatten
              </span>
            </>
          )}
        </div>
      )}

      {/* Call Test Component verwijderd - gebruik normale ConnectieFlow
          Audio calls: WebRTC (via "Bellen" knop)
          Video calls: Google Meet (via "Beeldbellen" knop) */}

      {/* Chat Messages - Neem beschikbare ruimte */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-1.5 space-y-1.5 min-h-0">
        {chatMessages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-xs">
            <p>Geen berichten. Start het gesprek!</p>
          </div>
        ) : (
          <>
            {chatMessages.map((msg) => {
              // ✅ Auto-detect old welcome messages and render as system
              const isOldWelcome = msg.message && (
                msg.message.includes('connectieverzoek is geaccepteerd') ||
                (msg.message.includes('Laten we chatten') && msg.message.includes('Hallo'))
              );
              
              // ✅ System messages: centered
              if (msg.messageType === 'system' || msg.senderId === 'system' || isOldWelcome) {
                const displayMessage = isOldWelcome && msg.messageType !== 'system'
                  ? '🎉 Jullie zijn nu verbonden! Start het gesprek.'
                  : msg.message;
                return (
                  <div key={msg.id} className="flex justify-center my-1">
                    <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-1 rounded-full text-[10px] text-center max-w-[90%]">
                      {displayMessage}
                    </div>
                  </div>
                );
              }
              
              return (
              <div
                key={msg.id}
                className={`flex items-end gap-2 ${msg.senderId === user?.uid ? 'flex-row-reverse' : 'flex-row'}`}
              >
                {/* Profile Photo */}
                <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 bg-gray-200 border-2 border-white shadow-sm">
                  {msg.senderId === user?.uid ? (
                    user?.photoURL ? (
                      <img 
                        src={user.photoURL} 
                        alt={user.displayName || 'Je'} 
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: 'center'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold">
                        {(user?.displayName || user?.email || 'U')[0].toUpperCase()}
                      </div>
                    )
                  ) : (
                    selectedConnectionData?.photoURL ? (
                      <img 
                        src={selectedConnectionData.photoURL} 
                        alt={selectedConnectionData.name || 'Ander'} 
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: selectedConnectionData.photoPosition 
                            ? `${selectedConnectionData.photoPosition.x}% ${selectedConnectionData.photoPosition.y}%` 
                            : 'center'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                        {(selectedConnectionData?.name || 'A')[0].toUpperCase()}
                      </div>
                    )
                  )}
                </div>

                {/* Video Call Link - Inline notification (iframe auto-shows) */}
                {msg.messageType === 'video_call_link' && msg.videoCallUrl ? (
                  <div className="max-w-xs w-full">
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 rounded-lg p-2 shadow-sm">
                      <div className="flex items-center space-x-1.5">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-sm">🎥</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-blue-900 text-[10px]">Video Call Gestart!</p>
                          <p className="text-[10px] text-blue-700">
                            De video call wordt automatisch geladen
                          </p>
                        </div>
                      </div>
                      <p className="text-[10px] text-blue-600 mt-1 text-right">
                        {formatChatTime(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Regular text message */
                  <div
                    className={`max-w-xs px-2 py-1.5 rounded-lg text-xs ${
                      msg.senderId === user?.uid
                        ? 'bg-gradient-to-r from-orange-50 to-orange-100 text-gray-800'
                        : 'bg-blue-50 text-gray-800 border border-blue-100'
                    }`}
                  >
                    <p>{msg.message}</p>
                    <p className={`text-[10px] mt-0.5 ${
                      msg.senderId === user?.uid ? 'text-orange-600' : 'text-blue-400'
                    }`}>
                      {formatChatTime(msg.timestamp)}
                    </p>
                  </div>
                )}
              </div>
              );
            })}
            <div ref={chatEndRef} />
          </>
        )}
      </div>

      {/* Chat Input - Altijd onderaan, altijd zichtbaar */}
      <div className="flex-shrink-0 px-1.5 pb-1.5 bg-white" style={{ position: 'relative', zIndex: 10 }}>
        <div className="flex space-x-1.5">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => {
              onNewMessageChange(e.target.value);
              handleTyping(); // ✅ Update presence when typing
            }}
            onKeyPress={onKeyPress}
            placeholder="Typ je bericht..."
            aria-label="Typ je bericht"
            aria-describedby="send-message-help"
            className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:border-orange-500"
          />
          <button
            onClick={onSendMessage}
            disabled={!newMessage.trim()}
            aria-label="Verstuur bericht"
            aria-disabled={!newMessage.trim()}
            className="bg-orange-500 text-white px-2 py-1.5 rounded-lg hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            <span className="text-xs">→</span>
          </button>
          <span id="send-message-help" className="sr-only">
            Druk op Enter of klik op de knop om je bericht te versturen
          </span>
        </div>
      </div>
    </div>
  );
};

export default ChatPhase;










